import { sessionApi } from '@/services/sessionApi'

const state = {
  sessionStatus: null, // null, waiting, active, completed
  participantStatus: null, // waiting, active, completed, left
  sessionId: null,
  pinCode: null,
  groupId: null,
  schoolGradeId: null,
  currentQuestionId: null,
  waitingCount: 0,
  participants: [],
  loading: false,
  error: null,
  polling: false,
  websocketConnected: false,
}

const getters = {
  getSessionStatus: (state) => state.sessionStatus,
  getParticipantStatus: (state) => state.participantStatus,
  getSessionId: (state) => state.sessionId,
  getPinCode: (state) => state.pinCode,
  getGroupId: (state) => state.groupId,
  getSchoolGradeId: (state) => state.schoolGradeId,
  getCurrentQuestionId: (state) => state.currentQuestionId,
  getWaitingCount: (state) => state.waitingCount,
  getParticipants: (state) => state.participants,
  isLoading: (state) => state.loading,
  getError: (state) => state.error,
  isPolling: (state) => state.polling,
  isWebsocketConnected: (state) => state.websocketConnected,
  canStartQuiz: (state) => {
    return state.sessionStatus === 'waiting' && state.waitingCount > 0
  },
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_SESSION_STATUS(state, status) {
    console.log('Setting session status:', status, 'Type:', typeof status)
    state.sessionStatus = status

    // Persist session status for browser refresh scenarios
    try {
      localStorage.setItem('session_status', status)
    } catch (error) {
      console.warn('Failed to persist session status to localStorage:', error)
    }
  },
  SET_PARTICIPANT_STATUS(state, status) {
    state.participantStatus = status
  },
  SET_SESSION_ID(state, sessionId) {
    state.sessionId = sessionId
  },
  SET_PIN_CODE(state, pinCode) {
    state.pinCode = pinCode
  },
  SET_GROUP_ID(state, groupId) {
    state.groupId = groupId

    // Persist group ID for browser refresh scenarios
    try {
      localStorage.setItem('session_groupId', groupId)
    } catch (error) {
      console.warn('Failed to persist group ID to localStorage:', error)
    }
  },
  SET_SCHOOL_GRADE_ID(state, schoolGradeId) {
    state.schoolGradeId = schoolGradeId
  },
  SET_CURRENT_QUESTION_ID(state, questionId) {
    state.currentQuestionId = questionId
  },
  SET_WAITING_COUNT(state, count) {
    state.waitingCount = count
  },
  SET_PARTICIPANTS(state, participants) {
    state.participants = participants
  },
  ADD_PARTICIPANT(state, participant) {
    const existingIndex = state.participants.findIndex((p) => p.id === participant.id)
    if (existingIndex >= 0) {
      state.participants.splice(existingIndex, 1, participant)
    } else {
      state.participants.push(participant)
    }
    // Update waiting count based on waiting participants
    state.waitingCount = state.participants.filter((p) => p.status === 'waiting').length
  },
  REMOVE_PARTICIPANT(state, participantId) {
    state.participants = state.participants.filter((p) => p.id !== participantId)
    state.waitingCount = state.participants.filter((p) => p.status === 'waiting').length
  },
  UPDATE_PARTICIPANT_STATUS(state, { participantId, status }) {
    const participant = state.participants.find((p) => p.id === participantId)
    if (participant) {
      participant.status = status
    }
    state.waitingCount = state.participants.filter((p) => p.status === 'waiting').length
  },
  SET_POLLING(state, polling) {
    state.polling = polling
  },
  SET_WEBSOCKET_CONNECTED(state, connected) {
    state.websocketConnected = connected
  },
  CLEAR_SESSION(state) {
    state.sessionStatus = null
    state.participantStatus = null
    state.sessionId = null
    state.pinCode = null
    state.groupId = null
    state.schoolGradeId = null
    state.currentQuestionId = null
    state.waitingCount = 0
    state.participants = []
    state.error = null

    // Clear persisted session data
    try {
      localStorage.removeItem('session_status')
      localStorage.removeItem('session_groupId')
    } catch (error) {
      console.warn('Failed to clear session data from localStorage:', error)
    }
  },

  RESTORE_SESSION_DATA(state, data) {
    // Restore session data from localStorage (e.g., after browser refresh)
    if (data.sessionStatus) {
      state.sessionStatus = data.sessionStatus
    }
    if (data.groupId) {
      state.groupId = data.groupId
    }
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
}

const actions = {
  // Join waiting room (Student)
  async joinWaitingRoom({ commit }, pinCode) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.accessWithPin(pinCode)

      if (response.success) {
        commit('SET_SESSION_STATUS', response.data.session_status)
        commit('SET_PARTICIPANT_STATUS', response.data.participant_status)
        commit('SET_SESSION_ID', response.data.session_id)
        commit('SET_PIN_CODE', pinCode)

        if (response.data.group_id) {
          commit('SET_GROUP_ID', response.data.group_id)
        }

        if (response.data.waiting_count !== undefined) {
          commit('SET_WAITING_COUNT', response.data.waiting_count)
        }

        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error joining waiting room'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Check session status (Student polling)
  async checkSessionStatus({ commit, state }) {
    if (!state.pinCode) return { success: false, error: 'No PIN code available' }

    try {
      const response = await sessionApi.getSessionStatus(state.pinCode)

      if (response.success) {
        commit('SET_SESSION_STATUS', response.data.session_status)

        if (response.data.participant_status) {
          commit('SET_PARTICIPANT_STATUS', response.data.participant_status)
        }

        if (response.data.group_id) {
          commit('SET_GROUP_ID', response.data.group_id)
        }

        if (response.data.waiting_count !== undefined) {
          commit('SET_WAITING_COUNT', response.data.waiting_count)
        }

        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error checking session status'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  // Get session details (Teacher)
  async getSessionDetails({ commit }, pinCode) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.getSession(pinCode)

      if (response.success) {
        commit('SET_SESSION_STATUS', response.data.session_status)
        commit('SET_SESSION_ID', response.data.session_id)
        commit('SET_PIN_CODE', pinCode)

        if (response.data.participants) {
          commit('SET_PARTICIPANTS', response.data.participants)
        }

        if (response.data.waiting_count !== undefined) {
          commit('SET_WAITING_COUNT', response.data.waiting_count)
        }

        if (response.data.school_grade_id) {
          commit('SET_SCHOOL_GRADE_ID', response.data.school_grade_id)
        } else {
          console.warn('⚠️ No school_grade_id found in response')
        }

        if (response.data.group_id) {
          commit('SET_GROUP_ID', response.data.group_id)
        } else {
          console.warn('⚠️ No group_id found in response')
        }

        if (response.data.current_question_id) {
          commit('SET_CURRENT_QUESTION_ID', response.data.current_question_id)
        }

        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error loading session details'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Start session (Teacher)
  async startSession({ commit, state }) {
    if (!state.pinCode) return { success: false, error: 'No PIN code available' }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.startSession(state.pinCode)

      if (response.success) {
        commit('SET_SESSION_STATUS', 'active')
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error starting session'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Complete session (Teacher)
  async completeSession({ commit, state }) {
    if (!state.pinCode) return { success: false, error: 'No PIN code available' }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.completeSession(state.pinCode)

      if (response.success) {
        commit('SET_SESSION_STATUS', 'completed')
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error completing session'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Remove student (Teacher)
  async removeStudent({ commit, state }, studentId) {
    if (!state.pinCode) return { success: false, error: 'No PIN code available' }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.removeStudent(state.pinCode, studentId)

      if (response.success) {
        commit('REMOVE_PARTICIPANT', studentId)
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error removing student'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // Leave session (Student)
  async leaveSession({ commit, state }) {
    if (!state.sessionId) return { success: false, error: 'No session ID available' }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.leaveSession(state.sessionId)

      if (response.success) {
        commit('SET_PARTICIPANT_STATUS', 'left')
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error leaving session'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },

  // WebSocket event handlers
  onStudentJoined({ commit }, participant) {
    commit('ADD_PARTICIPANT', participant)
  },

  onStudentLeft({ commit }, participantId) {
    commit('UPDATE_PARTICIPANT_STATUS', { participantId, status: 'left' })
  },

  onSessionStarted({ commit, state, rootGetters }, data) {
    console.log('onSessionStarted action called with data:', data)
    commit('SET_SESSION_STATUS', 'active')
    commit('SET_PARTICIPANT_STATUS', 'active')

    // Update group_id if provided in the WebSocket data
    if (data.group_id) {
      commit('SET_GROUP_ID', data.group_id)
    }

    // Update session_id if provided
    if (data.session_id) {
      commit('SET_SESSION_ID', data.session_id)
    }

    // Log the state after update for debugging
    console.log('Session state after onSessionStarted:', {
      sessionStatus: state.sessionStatus,
      participantStatus: state.participantStatus,
      groupId: state.groupId,
      sessionId: state.sessionId,
    })

    // Trigger navigation to evaluation
    // This will be handled by the component listening to state changes via watchers
  },

  onSessionCompleted({ commit }) {
    commit('SET_SESSION_STATUS', 'completed')
    commit('SET_PARTICIPANT_STATUS', 'completed')
  },

  // Control actions
  setPolling({ commit }, polling) {
    commit('SET_POLLING', polling)
  },

  setWebsocketConnected({ commit }, connected) {
    commit('SET_WEBSOCKET_CONNECTED', connected)
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  clearSession({ commit }) {
    commit('CLEAR_SESSION')
  },

  // Action to set session as active (when teacher starts quiz)
  activateSession({ commit }, { sessionId, groupId }) {
    console.log('Activating session:', { sessionId, groupId })
    commit('SET_SESSION_STATUS', 'active')

    if (sessionId) {
      commit('SET_SESSION_ID', sessionId)
    }

    if (groupId) {
      commit('SET_GROUP_ID', groupId)
    }

    return { success: true }
  },

  // Action to restore session data from localStorage (e.g., after page refresh)
  restoreSessionData({ commit }) {
    try {
      const sessionStatus = localStorage.getItem('session_status')
      const groupId = localStorage.getItem('session_groupId')

      if (sessionStatus || groupId) {
        console.log('Restoring session data from localStorage:', { sessionStatus, groupId })
        commit('RESTORE_SESSION_DATA', { sessionStatus, groupId })
        return { success: true, data: { sessionStatus, groupId } }
      }
    } catch (error) {
      console.warn('Failed to restore session data from localStorage:', error)
    }
    return { success: false, error: 'No valid stored session data' }
  },

  // Finish session (Student)
  async finishSession({ commit, state }) {
    if (!state.sessionId) return { success: false, error: 'No session ID available' }

    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await sessionApi.finishSession(state.sessionId)

      if (response.success) {
        commit('SET_PARTICIPANT_STATUS', 'completed')
        return { success: true, data: response.data }
      } else {
        commit('SET_ERROR', response.message)
        return { success: false, error: response.message }
      }
    } catch (error) {
      const errorMessage = error.message || 'Error finishing session'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
